import * as React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type RowMenuItem =
  // `checked` makes it a checkable item (a sort option, a toggle).
  | { kind: "item"; label: string; onSelect: () => void; checked?: boolean }
  | { kind: "label"; label: string }
  | { kind: "separator" }
  // A day to pick (labelled for screen readers, as nothing shows the label):
  // a date field and a Set button, starting at `max`.
  | {
      kind: "date";
      label: string;
      max: string;
      onSelect: (day: string) => void;
    }
  // A day the items below it act on: a date field, named by `label`, that
  // calls `onChange` as it changes (the items' own words follow it).
  | {
      kind: "day";
      label: string;
      value: string;
      max: string;
      onChange: (day: string) => void;
    };

// The menu's stops for the arrow keys: its items, and a date row's field.
const STOPS = "[role^=menuitem]:not(:disabled), input";

// A date row: the field takes the day, Enter or Set picks it.
const DateRow: React.FC<{
  label: string;
  max: string;
  onPick: (day: string) => void;
}> = ({ label, max, onPick }) => {
  const [day, setDay] = useState(max);
  return (
    <form
      className="ctx-menu-date"
      onSubmit={(e) => {
        e.preventDefault();
        if (day) onPick(day);
      }}
    >
      <input
        type="date"
        aria-label={label}
        max={max}
        value={day}
        onChange={(e) => setDay(e.target.value)}
      />
      <button
        type="submit"
        role="menuitem"
        className="ctx-menu-item"
        disabled={!day}
      >
        Set
      </button>
    </form>
  );
};

// Gap kept between the menu and the viewport edge.
const MARGIN = 8;

// A context menu anchored at (x, y) in viewport coordinates, following the
// WAI-ARIA menu pattern: the first item takes focus, arrow keys / Home / End
// move between items, Escape or Tab closes, and focus returns to `returnFocus`
// (the row or handle that opened it). Any click outside, scroll or resize
// dismisses it, like a native menu. In a date row's field the keys are the
// field's (arrows change the day, Tab moves on to Set) but for Escape.
const RowMenu: React.FC<{
  x: number;
  y: number;
  label: string;
  items: RowMenuItem[];
  returnFocus: HTMLElement | null;
  onClose: () => void;
}> = ({ x, y, label, items, returnFocus, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  const close = (restoreFocus: boolean) => {
    onClose();
    if (restoreFocus && returnFocus?.isConnected) returnFocus.focus();
  };

  // Flip left/up when the menu would overflow the viewport, then focus it.
  useLayoutEffect(() => {
    const el = ref.current!;
    const { width, height } = el.getBoundingClientRect();
    const left =
      x + width + MARGIN > window.innerWidth ? Math.max(MARGIN, x - width) : x;
    const top =
      y + height + MARGIN > window.innerHeight
        ? Math.max(MARGIN, y - height)
        : y;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.querySelector<HTMLElement>("[role^=menuitem]")?.focus();
  }, [x, y]);

  useEffect(() => {
    const dismiss = () => onClose();
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [onClose]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).tagName === "INPUT" && e.key !== "Escape")
      return;
    const buttons = Array.from(
      ref.current!.querySelectorAll<HTMLElement>(STOPS),
    );
    const at = buttons.indexOf(document.activeElement as HTMLElement);
    const focus = (i: number) =>
      buttons[(i + buttons.length) % buttons.length].focus();
    if (e.key === "ArrowDown") focus(at + 1);
    else if (e.key === "ArrowUp") focus(at - 1);
    else if (e.key === "Home") focus(0);
    else if (e.key === "End") focus(buttons.length - 1);
    else if (e.key === "Escape") close(true);
    else if (e.key === "Tab") close(false);
    else return;
    // Handled here: keep Escape from also clearing a selection, and arrows
    // from scrolling the page (which would dismiss the menu).
    if (e.key !== "Tab") e.preventDefault();
  };

  return createPortal(
    <>
      {/* Transparent layer: any click or right-click outside dismisses
          (Escape and Tab are the keyboard's way out). */}
      <div
        className="ctx-menu-backdrop"
        role="presentation"
        onClick={() => close(false)}
        onContextMenu={(e) => {
          e.preventDefault();
          close(false);
        }}
      />
      <div
        ref={ref}
        className="ctx-menu"
        role="menu"
        aria-label={label}
        tabIndex={-1}
        style={{ left: x, top: y }}
        onKeyDown={onKeyDown}
        onContextMenu={(e) => e.preventDefault()}
      >
        {items.map((item, i) =>
          item.kind === "separator" ? (
            <hr key={i} className="ctx-menu-sep" />
          ) : item.kind === "date" ? (
            <DateRow
              key={i}
              label={item.label}
              max={item.max}
              onPick={(day) => {
                close(true);
                item.onSelect(day);
              }}
            />
          ) : item.kind === "day" ? (
            <label key={i} className="ctx-menu-date ctx-menu-day">
              <span className="ctx-menu-label">{item.label}</span>
              <input
                type="date"
                max={item.max}
                value={item.value}
                onChange={(e) =>
                  e.target.value && item.onChange(e.target.value)
                }
              />
            </label>
          ) : item.kind === "label" ? (
            <div key={i} className="ctx-menu-label" role="presentation">
              {item.label}
            </div>
          ) : (
            <button
              key={i}
              type="button"
              role={
                item.checked === undefined ? "menuitem" : "menuitemcheckbox"
              }
              aria-checked={item.checked}
              tabIndex={-1}
              className={`ctx-menu-item${item.checked ? " checked" : ""}`}
              onClick={() => {
                close(true);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ),
        )}
      </div>
    </>,
    document.body,
  );
};

export default RowMenu;
